/** OnlyFans own-profile payloads → the My profile page (SHAPES.md 2026-10-08). */

type Raw = Record<string, unknown>

const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : 0)
const str = (v: unknown) => (typeof v === 'string' ? v : '')

function thumb(raw: Raw, field: string, thumbs: string, sizes: string[]): string {
  const set = (raw[thumbs] ?? {}) as Raw
  for (const s of sizes) if (str(set[s])) return str(set[s])
  return str(raw[field])
}

export interface Profile {
  id: number
  name: string
  username: string
  /** OnlyFans HTML. */
  about: string
  location: string
  website: string
  wishlist: string
  avatar: string
  header: string
  verified: boolean
  /** Available (true) / Invisible (false). */
  visibleOnline: boolean
  photos: number
  videos: number
  likes: number
  fans: number
  subscribePrice: number
}

export function toProfile(raw: Raw): Profile {
  return {
    id: num(raw.id),
    name: str(raw.name),
    username: str(raw.username),
    about: str(raw.about),
    location: str(raw.location),
    website: str(raw.website),
    wishlist: str(raw.wishlist),
    avatar: thumb(raw, 'avatar', 'avatarThumbs', ['c144', 'c50']),
    header: thumb(raw, 'header', 'headerThumbs', ['w760', 'w480']),
    verified: raw.isVerified === true,
    visibleOnline: raw.isVisibleOnline !== false,
    photos: num(raw.photosCount),
    videos: num(raw.videosCount),
    likes: num(raw.favoritedCount),
    fans: num(raw.subscribersCount),
    subscribePrice: num(raw.subscribePrice),
  }
}

/** The posts response `counters`: tab counts and the media chips. */
export interface Counters {
  posts: number
  medias: number
  photos: number
  videos: number
}

export function toCounters(raw: unknown): Counters | null {
  if (!raw || typeof raw !== 'object') return null
  const c = raw as Raw
  return { posts: num(c.postsCount), medias: num(c.mediasCount), photos: num(c.photosCount), videos: num(c.videosCount) }
}

export interface Label {
  id: string
  name: string
  posts: number
}

export function toLabel(raw: Raw): Label {
  return { id: String(raw.id), name: str(raw.name), posts: num(raw.postsCount) }
}

export interface Highlight {
  id: number
  title: string
  cover: string
  stories: number
}

export function toHighlight(raw: Raw): Highlight {
  return { id: num(raw.id), title: str(raw.title), cover: str(raw.cover), stories: num(raw.storiesCount) }
}

export type Tab = 'posts' | 'media'
export type MediaFilter = 'medias' | 'photos' | 'videos'
export type SortField = 'publish_date' | 'favorites_count' | 'tips_summ'
export type SortDir = 'desc' | 'asc'

export interface PostQuery {
  tab: Tab
  media: MediaFilter
  field: SortField
  dir: SortDir
  /** Custom label id (Posts tab chips). */
  label: string | null
  search: string
  /** Go to date: yyyy-mm-dd, local. */
  date: string | null
}

export const DEFAULT_QUERY: PostQuery = { tab: 'posts', media: 'medias', field: 'publish_date', dir: 'desc', label: null, search: '', date: null }

/** The plain newest-first view, where OnlyFans lists pinned posts on top. */
export function isDefaultView(q: PostQuery): boolean {
  return q.field === 'publish_date' && q.dir === 'desc' && !q.label && !q.search && !q.date
}

export function postsPath(userId: number, q: PostQuery): string {
  return q.tab === 'posts' ? `users/${userId}/posts` : `users/${userId}/posts/${q.media}`
}
